import React, { useEffect, useMemo, useRef, useState } from 'react'

import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Modal,
    Paper,
    Radio,
    RadioGroup,
    Slide,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import MailIcon from '@material-ui/icons/Mail'
import EmailIcon from '@material-ui/icons/Email'
import SmsIcon from '@material-ui/icons/Sms'
import InboxIcon from '@material-ui/icons/Inbox'
import SearchIcon from '@material-ui/icons/Search'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import PeopleIcon from '@material-ui/icons/People'
import PersonIcon from '@material-ui/icons/Person'
import HistoryIcon from '@material-ui/icons/History'
import DeleteIcon from '@material-ui/icons/Delete'
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile'

import { Container } from '../../components'
import { DropFiles } from '../../components/DropFiles/DropFiles'
import {
    AudienceType,
    AudiencePreviewResponse,
    LeaseScope,
    SelectableTenant,
} from '../../models/TenantMessage'
import {
    useTenantMessages,
    useTenantMessageTenants,
} from '../../hooks/useTenantMessages'
import { useTenantMessageStyles } from './styles'

interface Props {
    open: boolean
    handleClose: () => void
}

const LEASE_STATUS_MAP: Record<number, { label: string; color: string }> = {
    1: { label: 'Applicant', color: '#9ca3af' },
    3: { label: 'Future', color: '#8b5cf6' },
    4: { label: 'Current', color: '#10b981' },
    5: { label: 'Notice', color: '#f59e0b' },
    6: { label: 'Past', color: '#6b7280' },
}

const DEFAULT_STATUS_FILTERS = [4, 5]

const SCOPE_OPTIONS: Array<{
    value: LeaseScope
    label: string
    description: string
    icon: JSX.Element
}> = [
    {
        value: 'CURRENT',
        label: 'Current & Notice',
        description: 'Tenants with an active or ending lease',
        icon: <PeopleIcon fontSize="small" />,
    },
    {
        value: 'CURRENT_AND_FUTURE',
        label: 'Current, Notice & Future',
        description: 'Includes upcoming move-ins',
        icon: <PersonIcon fontSize="small" />,
    },
    {
        value: 'FUTURE',
        label: 'Future',
        description: 'Only tenants with an upcoming lease',
        icon: <HistoryIcon fontSize="small" />,
    },
]

export const ComposeMessageModal: React.FC<Props> = ({ open, handleClose }) => {
    const classes = useTenantMessageStyles()
    const theme = useTheme()
    const { sendMessage, audiencePreview } = useTenantMessages()
    const { tenants, isLoading: tenantsLoading } = useTenantMessageTenants()

    const [subject, setSubject] = useState('')
    const [body, setBody] = useState('')
    const [audienceType, setAudienceType] = useState<AudienceType>('ALL')
    const [leaseScope, setLeaseScope] = useState<LeaseScope>('CURRENT')
    const [selectedLeaseIds, setSelectedLeaseIds] = useState<number[]>([])
    const [searchText, setSearchText] = useState('')
    const [statusFilters, setStatusFilters] = useState<number[]>(
        DEFAULT_STATUS_FILTERS,
    )

    const [contentCollapsed, setContentCollapsed] = useState(false)
    const [attachments, setAttachments] = useState<File[]>([])
    const [clientToken, setClientToken] = useState('')

    const [sendEmail, setSendEmail] = useState(false)
    const [sendSms, setSendSms] = useState(false)
    const [emailSenderName, setEmailSenderName] = useState('')
    const [emailReplyTo, setEmailReplyTo] = useState('')
    const [
        previewInfo,
        setPreviewInfo,
    ] = useState<AudiencePreviewResponse | null>(null)

    const [sending, setSending] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)

    const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const generateToken = (): string => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0
            const v = c === 'x' ? r : (r & 0x3) | 0x8
            return v.toString(16)
        })
    }

    useEffect(() => {
        if (open) {
            setSubject('')
            setBody('')
            setAudienceType('ALL')
            setLeaseScope('CURRENT')
            setSelectedLeaseIds([])
            setSearchText('')
            setStatusFilters(DEFAULT_STATUS_FILTERS)
            setContentCollapsed(false)
            setAttachments([])
            setClientToken(generateToken())
            setSendEmail(false)
            setSendSms(false)
            setEmailSenderName('')
            setEmailReplyTo('')
            setPreviewInfo(null)
            setSending(false)
        }
    }, [open])

    useEffect(() => {
        if (!open || (!sendEmail && !sendSms)) {
            setPreviewInfo(null)
            return
        }
        if (previewTimerRef.current) {
            clearTimeout(previewTimerRef.current)
        }
        previewTimerRef.current = setTimeout(() => {
            audiencePreview.mutate(
                {
                    audience_type: audienceType,
                    lease_scope:
                        audienceType === 'ALL' ? leaseScope : undefined,
                    lease_ids:
                        audienceType === 'SELECTED' ? selectedLeaseIds : [],
                },
                {
                    onSuccess: (data) => setPreviewInfo(data),
                },
            )
        }, 400)
        return () => {
            if (previewTimerRef.current) clearTimeout(previewTimerRef.current)
        }
    }, [open, sendEmail, sendSms, audienceType, leaseScope, selectedLeaseIds])

    const filteredTenants = useMemo(() => {
        return tenants.filter((t) => {
            if (
                statusFilters.length > 0 &&
                !statusFilters.includes(t.lease_status)
            ) {
                return false
            }
            if (!searchText.trim()) return true
            const q = searchText.toLowerCase()
            return (
                t.name.toLowerCase().includes(q) ||
                t.unit_label.toLowerCase().includes(q) ||
                t.email.toLowerCase().includes(q) ||
                t.area_label?.toLowerCase().includes(q) ||
                t.area_config_name?.toLowerCase().includes(q)
            )
        })
    }, [tenants, statusFilters, searchText])

    const canSend =
        subject.trim().length > 0 &&
        body.trim().length > 0 &&
        (audienceType === 'ALL' || selectedLeaseIds.length > 0)

    const handleSend = () => {
        if (!canSend) return
        setConfirmOpen(true)
    }

    const confirmSend = () => {
        setConfirmOpen(false)
        setSending(true)
        sendMessage.mutate(
            {
                subject: subject.trim(),
                body: body.trim(),
                audience_type: audienceType,
                lease_scope: audienceType === 'ALL' ? leaseScope : undefined,
                lease_ids: audienceType === 'SELECTED' ? selectedLeaseIds : [],
                client_token: clientToken,
                send_email: sendEmail,
                send_sms: sendSms,
                email_sender_name:
                    sendEmail && emailSenderName.trim()
                        ? emailSenderName.trim()
                        : undefined,
                email_reply_to:
                    sendEmail && emailReplyTo.trim()
                        ? emailReplyTo.trim()
                        : undefined,
                attachments: attachments.length > 0 ? attachments : undefined,
            },
            {
                onSuccess: () => {
                    handleClose()
                },
                onSettled: () => {
                    setSending(false)
                },
            },
        )
    }

    const toggleTenant = (tenant: SelectableTenant) => {
        setSelectedLeaseIds((prev) =>
            prev.includes(tenant.lease_id)
                ? prev.filter((id) => id !== tenant.lease_id)
                : [...prev, tenant.lease_id],
        )
    }

    const toggleStatusFilter = (status: number) => {
        setStatusFilters((prev) =>
            prev.includes(status)
                ? prev.filter((s) => s !== status)
                : [...prev, status],
        )
    }

    const selectAll = () => {
        const allIds = filteredTenants.map((t) => t.lease_id)
        setSelectedLeaseIds((prev) => {
            const combined = new Set([...prev, ...allIds])
            return Array.from(combined)
        })
    }

    const deselectAll = () => {
        const filteredIds = new Set(filteredTenants.map((t) => t.lease_id))
        setSelectedLeaseIds((prev) => prev.filter((id) => !filteredIds.has(id)))
    }

    const allFilteredSelected =
        filteredTenants.length > 0 &&
        filteredTenants.every((t) => selectedLeaseIds.includes(t.lease_id))

    const removeAttachment = (index: number) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index))
    }

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }

    return (
        <>
            <Modal
                open={open}
                onClose={handleClose}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Slide direction="up" in={open}>
                    <Paper className={classes.paper}>
                        <Container
                            direction="column"
                            style={{
                                width: 'min(1060px, calc(100vw - 60px))',
                                height: 'calc(100vh - 80px)',
                                maxHeight: 860,
                                overflow: 'hidden',
                            }}
                        >
                            {/* Header */}
                            <div className={classes.header}>
                                <div className={classes.headerIcon}>
                                    <MailIcon />
                                </div>
                                <Container direction="column" flex={1}>
                                    <span className={classes.headerTitle}>
                                        New Message
                                    </span>
                                    <span className={classes.headerSubtitle}>
                                        Compose and send a one-way message to
                                        tenants
                                    </span>
                                </Container>
                                <IconButton size="small" onClick={handleClose}>
                                    <CloseIcon />
                                </IconButton>
                            </div>

                            {/* Body */}
                            <div className={classes.body}>
                                {/* Message Content - collapsible */}
                                <div className={classes.section}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() =>
                                            setContentCollapsed((c) => !c)
                                        }
                                    >
                                        <Box>
                                            <Typography
                                                className={classes.sectionTitle}
                                                style={{ marginBottom: 0 }}
                                            >
                                                Message Content
                                            </Typography>
                                            {contentCollapsed &&
                                                subject.trim() && (
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        {subject}
                                                    </Typography>
                                                )}
                                        </Box>
                                        <IconButton size="small">
                                            {contentCollapsed ? (
                                                <ExpandMoreIcon />
                                            ) : (
                                                <ExpandLessIcon />
                                            )}
                                        </IconButton>
                                    </Box>
                                    <Collapse in={!contentCollapsed}>
                                        <Box mt={1.5}>
                                            <TextField
                                                label="Subject"
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                value={subject}
                                                onChange={(e) =>
                                                    setSubject(
                                                        e.target.value.slice(
                                                            0,
                                                            255,
                                                        ),
                                                    )
                                                }
                                                inputProps={{ maxLength: 255 }}
                                                helperText={`${subject.length}/255`}
                                                style={{ marginBottom: 12 }}
                                            />
                                            <TextField
                                                label="Body"
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                multiline
                                                rows={4}
                                                rowsMax={8}
                                                value={body}
                                                onChange={(e) =>
                                                    setBody(e.target.value)
                                                }
                                                placeholder="Enter your message to tenants..."
                                            />

                                            {/* Attachments */}
                                            <Box mt={1.5}>
                                                {attachments.length > 0 && (
                                                    <Box
                                                        mb={1}
                                                        display="flex"
                                                        flexDirection="column"
                                                        gridGap={6}
                                                    >
                                                        {attachments.map(
                                                            (file, idx) => (
                                                                <Box
                                                                    key={`${file.name}-${idx}`}
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    gridGap={8}
                                                                    style={{
                                                                        padding:
                                                                            '6px 10px',
                                                                        borderRadius: 6,
                                                                        border:
                                                                            '1px solid #e6e9ed',
                                                                        backgroundColor:
                                                                            '#fafbfc',
                                                                    }}
                                                                >
                                                                    <InsertDriveFileIcon
                                                                        fontSize="small"
                                                                        style={{
                                                                            color:
                                                                                '#6b7280',
                                                                        }}
                                                                    />
                                                                    <Typography
                                                                        variant="caption"
                                                                        style={{
                                                                            flex: 1,
                                                                            fontWeight: 500,
                                                                        }}
                                                                    >
                                                                        {
                                                                            file.name
                                                                        }
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="textSecondary"
                                                                    >
                                                                        {formatFileSize(
                                                                            file.size,
                                                                        )}
                                                                    </Typography>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() =>
                                                                            removeAttachment(
                                                                                idx,
                                                                            )
                                                                        }
                                                                        style={{
                                                                            padding: 2,
                                                                        }}
                                                                    >
                                                                        <DeleteIcon
                                                                            fontSize="small"
                                                                            style={{
                                                                                fontSize: 16,
                                                                            }}
                                                                        />
                                                                    </IconButton>
                                                                </Box>
                                                            ),
                                                        )}
                                                    </Box>
                                                )}
                                                <DropFiles
                                                    displayString="Drag and drop files here, or click to browse"
                                                    multipleFiles
                                                    onDrop={(files) =>
                                                        setAttachments(
                                                            (prev) => [
                                                                ...prev,
                                                                ...files,
                                                            ],
                                                        )
                                                    }
                                                />
                                            </Box>

                                            {/* Delivery Channels */}
                                            <Box mt={2}>
                                                <Typography
                                                    variant="caption"
                                                    style={{
                                                        fontWeight: 600,
                                                        display: 'block',
                                                        marginBottom: 8,
                                                        textTransform:
                                                            'uppercase',
                                                        letterSpacing: '0.04em',
                                                        fontSize: '0.625rem',
                                                    }}
                                                >
                                                    Delivery Channels
                                                </Typography>
                                                <Box
                                                    display="flex"
                                                    flexDirection="column"
                                                    gridGap={4}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked
                                                                disabled
                                                                size="small"
                                                                color="primary"
                                                            />
                                                        }
                                                        label={
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                gridGap={6}
                                                            >
                                                                <InboxIcon
                                                                    style={{
                                                                        fontSize: 16,
                                                                        color:
                                                                            '#008C85',
                                                                    }}
                                                                />
                                                                <Typography
                                                                    variant="body2"
                                                                    style={{
                                                                        fontSize:
                                                                            '0.8125rem',
                                                                    }}
                                                                >
                                                                    Portal
                                                                    (always on)
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                    >
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={
                                                                        sendEmail
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) =>
                                                                        setSendEmail(
                                                                            e
                                                                                .target
                                                                                .checked,
                                                                        )
                                                                    }
                                                                    size="small"
                                                                    color="primary"
                                                                />
                                                            }
                                                            label={
                                                                <Box
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    gridGap={6}
                                                                >
                                                                    <EmailIcon
                                                                        style={{
                                                                            fontSize: 16,
                                                                            color: sendEmail
                                                                                ? '#008C85'
                                                                                : '#9ca3af',
                                                                        }}
                                                                    />
                                                                    <Typography
                                                                        variant="body2"
                                                                        style={{
                                                                            fontSize:
                                                                                '0.8125rem',
                                                                        }}
                                                                    >
                                                                        Also
                                                                        send via
                                                                        Email
                                                                    </Typography>
                                                                </Box>
                                                            }
                                                        />
                                                        {sendEmail &&
                                                            previewInfo && (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="textSecondary"
                                                                    style={{
                                                                        marginLeft: 4,
                                                                    }}
                                                                >
                                                                    (
                                                                    {
                                                                        previewInfo.email_reachable
                                                                    }{' '}
                                                                    of{' '}
                                                                    {
                                                                        previewInfo.count
                                                                    }{' '}
                                                                    reachable)
                                                                </Typography>
                                                            )}
                                                    </Box>
                                                    {sendEmail && (
                                                        <Box
                                                            ml={4}
                                                            mb={1}
                                                            display="flex"
                                                            gridGap={12}
                                                        >
                                                            <TextField
                                                                label="Sender Name"
                                                                variant="outlined"
                                                                size="small"
                                                                value={
                                                                    emailSenderName
                                                                }
                                                                onChange={(e) =>
                                                                    setEmailSenderName(
                                                                        e.target.value.slice(
                                                                            0,
                                                                            255,
                                                                        ),
                                                                    )
                                                                }
                                                                placeholder="e.g. Sunset Apartments"
                                                                style={{
                                                                    flex: 1,
                                                                }}
                                                                inputProps={{
                                                                    maxLength: 255,
                                                                }}
                                                            />
                                                            <TextField
                                                                label="Reply-To Email"
                                                                variant="outlined"
                                                                size="small"
                                                                value={
                                                                    emailReplyTo
                                                                }
                                                                onChange={(e) =>
                                                                    setEmailReplyTo(
                                                                        e.target
                                                                            .value,
                                                                    )
                                                                }
                                                                placeholder="e.g. office@property.com"
                                                                style={{
                                                                    flex: 1,
                                                                }}
                                                                type="email"
                                                            />
                                                        </Box>
                                                    )}
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                    >
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={
                                                                        sendSms
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) =>
                                                                        setSendSms(
                                                                            e
                                                                                .target
                                                                                .checked,
                                                                        )
                                                                    }
                                                                    size="small"
                                                                    color="primary"
                                                                />
                                                            }
                                                            label={
                                                                <Box
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    gridGap={6}
                                                                >
                                                                    <SmsIcon
                                                                        style={{
                                                                            fontSize: 16,
                                                                            color: sendSms
                                                                                ? '#008C85'
                                                                                : '#9ca3af',
                                                                        }}
                                                                    />
                                                                    <Typography
                                                                        variant="body2"
                                                                        style={{
                                                                            fontSize:
                                                                                '0.8125rem',
                                                                        }}
                                                                    >
                                                                        Also
                                                                        send via
                                                                        SMS
                                                                    </Typography>
                                                                </Box>
                                                            }
                                                        />
                                                        {sendSms &&
                                                            previewInfo && (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="textSecondary"
                                                                    style={{
                                                                        marginLeft: 4,
                                                                    }}
                                                                >
                                                                    (
                                                                    {
                                                                        previewInfo.sms_reachable
                                                                    }{' '}
                                                                    of{' '}
                                                                    {
                                                                        previewInfo.count
                                                                    }{' '}
                                                                    reachable)
                                                                </Typography>
                                                            )}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Collapse>
                                </div>

                                {/* Audience */}
                                <div className={classes.section}>
                                    <Typography
                                        className={classes.sectionTitle}
                                    >
                                        Recipients
                                    </Typography>

                                    {/* Audience type selector - card style */}
                                    <Box display="flex" gridGap={12} mb={2}>
                                        <Box
                                            className={classes.audienceCard}
                                            onClick={() => {
                                                setAudienceType('ALL')
                                            }}
                                            style={{
                                                borderColor:
                                                    audienceType === 'ALL'
                                                        ? theme.palette.primary
                                                              .main
                                                        : undefined,
                                                backgroundColor:
                                                    audienceType === 'ALL'
                                                        ? 'rgba(0,140,133,0.04)'
                                                        : undefined,
                                            }}
                                        >
                                            <Radio
                                                checked={audienceType === 'ALL'}
                                                color="primary"
                                                size="small"
                                                style={{
                                                    padding: 4,
                                                    marginRight: 8,
                                                }}
                                            />
                                            <Box>
                                                <Typography
                                                    variant="body2"
                                                    style={{ fontWeight: 600 }}
                                                >
                                                    All Tenants
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                >
                                                    Send to everyone based on
                                                    lease scope
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box
                                            className={classes.audienceCard}
                                            onClick={() => {
                                                setAudienceType('SELECTED')
                                            }}
                                            style={{
                                                borderColor:
                                                    audienceType === 'SELECTED'
                                                        ? theme.palette.primary
                                                              .main
                                                        : undefined,
                                                backgroundColor:
                                                    audienceType === 'SELECTED'
                                                        ? 'rgba(0,140,133,0.04)'
                                                        : undefined,
                                            }}
                                        >
                                            <Radio
                                                checked={
                                                    audienceType === 'SELECTED'
                                                }
                                                color="primary"
                                                size="small"
                                                style={{
                                                    padding: 4,
                                                    marginRight: 8,
                                                }}
                                            />
                                            <Box>
                                                <Typography
                                                    variant="body2"
                                                    style={{ fontWeight: 600 }}
                                                >
                                                    Select Tenants
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                >
                                                    Choose specific tenants to
                                                    message
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* ALL: Lease scope cards */}
                                    {audienceType === 'ALL' && (
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                style={{
                                                    fontWeight: 600,
                                                    display: 'block',
                                                    marginBottom: 8,
                                                }}
                                            >
                                                Who counts as &ldquo;all&rdquo;?
                                            </Typography>
                                            <RadioGroup
                                                value={leaseScope}
                                                onChange={(e) => {
                                                    setLeaseScope(
                                                        e.target
                                                            .value as LeaseScope,
                                                    )
                                                }}
                                            >
                                                <Box
                                                    display="flex"
                                                    flexDirection="column"
                                                    gridGap={8}
                                                >
                                                    {SCOPE_OPTIONS.map(
                                                        (opt) => (
                                                            <Box
                                                                key={opt.value}
                                                                className={
                                                                    classes.scopeCard
                                                                }
                                                                onClick={() => {
                                                                    setLeaseScope(
                                                                        opt.value,
                                                                    )
                                                                }}
                                                                style={{
                                                                    borderColor:
                                                                        leaseScope ===
                                                                        opt.value
                                                                            ? theme
                                                                                  .palette
                                                                                  .primary
                                                                                  .main
                                                                            : undefined,
                                                                    backgroundColor:
                                                                        leaseScope ===
                                                                        opt.value
                                                                            ? 'rgba(0,140,133,0.04)'
                                                                            : undefined,
                                                                }}
                                                            >
                                                                <Radio
                                                                    checked={
                                                                        leaseScope ===
                                                                        opt.value
                                                                    }
                                                                    color="primary"
                                                                    size="small"
                                                                    style={{
                                                                        padding: 4,
                                                                        marginRight: 10,
                                                                    }}
                                                                />
                                                                <Box
                                                                    style={{
                                                                        display:
                                                                            'flex',
                                                                        alignItems:
                                                                            'center',
                                                                        gap: 10,
                                                                        flex: 1,
                                                                    }}
                                                                >
                                                                    <Box
                                                                        style={{
                                                                            width: 32,
                                                                            height: 32,
                                                                            borderRadius: 8,
                                                                            display:
                                                                                'flex',
                                                                            alignItems:
                                                                                'center',
                                                                            justifyContent:
                                                                                'center',
                                                                            backgroundColor:
                                                                                leaseScope ===
                                                                                opt.value
                                                                                    ? 'rgba(0,140,133,0.12)'
                                                                                    : '#f3f4f6',
                                                                            color:
                                                                                leaseScope ===
                                                                                opt.value
                                                                                    ? '#008C85'
                                                                                    : '#6b7280',
                                                                        }}
                                                                    >
                                                                        {
                                                                            opt.icon
                                                                        }
                                                                    </Box>
                                                                    <Box>
                                                                        <Typography
                                                                            variant="body2"
                                                                            style={{
                                                                                fontWeight: 600,
                                                                            }}
                                                                        >
                                                                            {
                                                                                opt.label
                                                                            }
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="caption"
                                                                            color="textSecondary"
                                                                        >
                                                                            {
                                                                                opt.description
                                                                            }
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        ),
                                                    )}
                                                </Box>
                                            </RadioGroup>
                                        </Box>
                                    )}

                                    {/* SELECTED: Tenant picker */}
                                    {audienceType === 'SELECTED' && (
                                        <Box>
                                            {/* Status filter chips */}
                                            <Box
                                                display="flex"
                                                flexWrap="wrap"
                                                gridGap={6}
                                                mb={1.5}
                                            >
                                                {Object.entries(
                                                    LEASE_STATUS_MAP,
                                                ).map(
                                                    ([
                                                        statusStr,
                                                        { label, color },
                                                    ]) => {
                                                        const status = Number(
                                                            statusStr,
                                                        )
                                                        const active = statusFilters.includes(
                                                            status,
                                                        )
                                                        return (
                                                            <Chip
                                                                key={status}
                                                                label={label}
                                                                size="small"
                                                                clickable
                                                                onClick={() =>
                                                                    toggleStatusFilter(
                                                                        status,
                                                                    )
                                                                }
                                                                style={{
                                                                    backgroundColor: active
                                                                        ? `${color}18`
                                                                        : undefined,
                                                                    color: active
                                                                        ? color
                                                                        : undefined,
                                                                    borderColor: active
                                                                        ? color
                                                                        : undefined,
                                                                    fontWeight: active
                                                                        ? 600
                                                                        : 400,
                                                                    fontSize:
                                                                        '0.6875rem',
                                                                }}
                                                                variant={
                                                                    active
                                                                        ? 'outlined'
                                                                        : 'default'
                                                                }
                                                            />
                                                        )
                                                    },
                                                )}
                                            </Box>

                                            {/* Search + bulk actions */}
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                gridGap={8}
                                                mb={1}
                                            >
                                                <TextField
                                                    placeholder="Search by name, unit, or area..."
                                                    variant="outlined"
                                                    size="small"
                                                    value={searchText}
                                                    onChange={(e) =>
                                                        setSearchText(
                                                            e.target.value,
                                                        )
                                                    }
                                                    style={{ flex: 1 }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <SearchIcon
                                                                    fontSize="small"
                                                                    color="disabled"
                                                                />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={
                                                        allFilteredSelected
                                                            ? deselectAll
                                                            : selectAll
                                                    }
                                                    style={{
                                                        textTransform: 'none',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                    disabled={
                                                        filteredTenants.length ===
                                                        0
                                                    }
                                                >
                                                    {allFilteredSelected
                                                        ? 'Deselect All'
                                                        : 'Select All'}
                                                </Button>
                                            </Box>

                                            {/* Selected count */}
                                            {selectedLeaseIds.length > 0 && (
                                                <Typography
                                                    variant="caption"
                                                    style={{
                                                        display: 'block',
                                                        marginBottom: 8,
                                                        fontWeight: 600,
                                                        color:
                                                            theme.palette
                                                                .primary.main,
                                                    }}
                                                >
                                                    {selectedLeaseIds.length}{' '}
                                                    tenant
                                                    {selectedLeaseIds.length !==
                                                    1
                                                        ? 's'
                                                        : ''}{' '}
                                                    selected
                                                </Typography>
                                            )}

                                            {/* Tenant table */}
                                            {tenantsLoading ? (
                                                <Box
                                                    display="flex"
                                                    justifyContent="center"
                                                    py={3}
                                                >
                                                    <CircularProgress
                                                        size={24}
                                                    />
                                                </Box>
                                            ) : (
                                                <TableContainer
                                                    style={{
                                                        maxHeight: 260,
                                                        border:
                                                            '1px solid #e6e9ed',
                                                        borderRadius: 8,
                                                    }}
                                                >
                                                    <Table
                                                        size="small"
                                                        stickyHeader
                                                    >
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell
                                                                    padding="checkbox"
                                                                    style={{
                                                                        width: 40,
                                                                    }}
                                                                >
                                                                    <Checkbox
                                                                        size="small"
                                                                        color="primary"
                                                                        checked={
                                                                            allFilteredSelected &&
                                                                            filteredTenants.length >
                                                                                0
                                                                        }
                                                                        indeterminate={
                                                                            selectedLeaseIds.length >
                                                                                0 &&
                                                                            !allFilteredSelected &&
                                                                            filteredTenants.some(
                                                                                (
                                                                                    t,
                                                                                ) =>
                                                                                    selectedLeaseIds.includes(
                                                                                        t.lease_id,
                                                                                    ),
                                                                            )
                                                                        }
                                                                        onChange={
                                                                            allFilteredSelected
                                                                                ? deselectAll
                                                                                : selectAll
                                                                        }
                                                                    />
                                                                </TableCell>
                                                                <TableCell
                                                                    style={{
                                                                        fontWeight: 600,
                                                                        fontSize:
                                                                            '0.75rem',
                                                                    }}
                                                                >
                                                                    Tenant
                                                                </TableCell>
                                                                <TableCell
                                                                    style={{
                                                                        fontWeight: 600,
                                                                        fontSize:
                                                                            '0.75rem',
                                                                    }}
                                                                >
                                                                    Unit
                                                                </TableCell>
                                                                <TableCell
                                                                    style={{
                                                                        fontWeight: 600,
                                                                        fontSize:
                                                                            '0.75rem',
                                                                    }}
                                                                >
                                                                    Status
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {filteredTenants.length ===
                                                            0 ? (
                                                                <TableRow>
                                                                    <TableCell
                                                                        colSpan={
                                                                            4
                                                                        }
                                                                        align="center"
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            color="textSecondary"
                                                                            style={{
                                                                                padding:
                                                                                    '16px 0',
                                                                            }}
                                                                        >
                                                                            No
                                                                            tenants
                                                                            match
                                                                            your
                                                                            filters
                                                                        </Typography>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ) : (
                                                                filteredTenants.map(
                                                                    (t) => {
                                                                        const statusInfo =
                                                                            LEASE_STATUS_MAP[
                                                                                t
                                                                                    .lease_status
                                                                            ]
                                                                        return (
                                                                            <TableRow
                                                                                key={
                                                                                    t.lease_id
                                                                                }
                                                                                hover
                                                                                onClick={() =>
                                                                                    toggleTenant(
                                                                                        t,
                                                                                    )
                                                                                }
                                                                                style={{
                                                                                    cursor:
                                                                                        'pointer',
                                                                                }}
                                                                            >
                                                                                <TableCell padding="checkbox">
                                                                                    <Checkbox
                                                                                        size="small"
                                                                                        color="primary"
                                                                                        checked={selectedLeaseIds.includes(
                                                                                            t.lease_id,
                                                                                        )}
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        style={{
                                                                                            fontWeight: 600,
                                                                                        }}
                                                                                    >
                                                                                        {
                                                                                            t.name
                                                                                        }
                                                                                    </Typography>
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        color="textSecondary"
                                                                                    >
                                                                                        {
                                                                                            t.email
                                                                                        }
                                                                                    </Typography>
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <Typography variant="caption">
                                                                                        {
                                                                                            t.unit_label
                                                                                        }
                                                                                        {t.area_config_name &&
                                                                                            ` · ${t.area_config_name}`}
                                                                                        {t.area_label &&
                                                                                            ` ${t.area_label}`}
                                                                                    </Typography>
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    {statusInfo && (
                                                                                        <Chip
                                                                                            label={
                                                                                                statusInfo.label
                                                                                            }
                                                                                            size="small"
                                                                                            style={{
                                                                                                backgroundColor: `${statusInfo.color}18`,
                                                                                                color:
                                                                                                    statusInfo.color,
                                                                                                fontWeight: 600,
                                                                                                fontSize:
                                                                                                    '0.625rem',
                                                                                                height: 22,
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        )
                                                                    },
                                                                )
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            )}
                                        </Box>
                                    )}
                                </div>
                            </div>

                            {/* Footer */}
                            <div className={classes.footer}>
                                <Button
                                    className={classes.cancelButton}
                                    onClick={handleClose}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    className={classes.sendButton}
                                    variant="contained"
                                    onClick={handleSend}
                                    disabled={!canSend || sending}
                                    startIcon={
                                        sending ? (
                                            <CircularProgress
                                                size={16}
                                                color="inherit"
                                            />
                                        ) : undefined
                                    }
                                >
                                    {sending ? 'Sending...' : 'Send Message'}
                                </Button>
                            </div>
                        </Container>
                    </Paper>
                </Slide>
            </Modal>

            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                PaperProps={{
                    style: {
                        borderRadius: 12,
                        padding: 8,
                        minWidth: 400,
                        maxWidth: 480,
                    },
                }}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    pt={3}
                    pb={1}
                    px={3}
                >
                    <Box
                        style={{
                            width: 48,
                            height: 48,
                            borderRadius: 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(0,140,133,0.1)',
                            color: '#008C85',
                            marginBottom: 16,
                        }}
                    >
                        <MailIcon />
                    </Box>
                    <Typography
                        variant="h6"
                        style={{
                            fontWeight: 700,
                            marginBottom: 8,
                            textAlign: 'center',
                        }}
                    >
                        Send Message?
                    </Typography>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ textAlign: 'center', lineHeight: 1.5 }}
                    >
                        {audienceType === 'ALL'
                            ? `This will deliver your message to all tenants matching the selected scope.`
                            : `This will deliver your message to ${
                                  selectedLeaseIds.length
                              } selected tenant${
                                  selectedLeaseIds.length !== 1 ? 's' : ''
                              }.`}
                    </Typography>
                </Box>

                <Box px={3} pt={2} pb={1}>
                    <Box
                        style={{
                            borderRadius: 8,
                            border: '1px solid #e6e9ed',
                            backgroundColor: '#fafbfc',
                            padding: '12px 16px',
                        }}
                    >
                        <Typography
                            variant="caption"
                            color="textSecondary"
                            style={{
                                display: 'block',
                                marginBottom: 4,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                fontSize: '0.625rem',
                            }}
                        >
                            Subject
                        </Typography>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {subject}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="textSecondary"
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 4,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                fontSize: '0.625rem',
                            }}
                        >
                            Message
                        </Typography>
                        <Typography
                            variant="body2"
                            style={{
                                whiteSpace: 'pre-wrap',
                                maxHeight: 160,
                                overflowY: 'auto',
                            }}
                        >
                            {body}
                        </Typography>
                        {attachments.length > 0 && (
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{ display: 'block', marginTop: 10 }}
                            >
                                {attachments.length} file
                                {attachments.length !== 1 ? 's' : ''} attached
                            </Typography>
                        )}

                        <Typography
                            variant="caption"
                            color="textSecondary"
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 0,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                fontSize: '0.625rem',
                            }}
                        >
                            Channels
                        </Typography>
                        <Box
                            display="flex"
                            alignItems="center"
                            gridGap={8}
                            mt={0.5}
                        >
                            <Chip
                                label="Portal"
                                size="small"
                                style={{
                                    height: 22,
                                    fontSize: '0.625rem',
                                    fontWeight: 600,
                                    backgroundColor: 'rgba(0,140,133,0.1)',
                                    color: '#008C85',
                                }}
                            />
                            {sendEmail && (
                                <Chip
                                    label={
                                        previewInfo
                                            ? `Email (${previewInfo.email_reachable} reachable)`
                                            : 'Email'
                                    }
                                    size="small"
                                    style={{
                                        height: 22,
                                        fontSize: '0.625rem',
                                        fontWeight: 600,
                                        backgroundColor: 'rgba(0,140,133,0.1)',
                                        color: '#008C85',
                                    }}
                                />
                            )}
                            {sendSms && (
                                <Chip
                                    label={
                                        previewInfo
                                            ? `SMS (${previewInfo.sms_reachable} reachable)`
                                            : 'SMS'
                                    }
                                    size="small"
                                    style={{
                                        height: 22,
                                        fontSize: '0.625rem',
                                        fontWeight: 600,
                                        backgroundColor: 'rgba(0,140,133,0.1)',
                                        color: '#008C85',
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                </Box>

                <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{
                        display: 'block',
                        textAlign: 'center',
                        padding: '8px 24px 0',
                    }}
                >
                    This action cannot be undone.
                </Typography>

                <DialogActions style={{ padding: '16px 24px 20px', gap: 8 }}>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        variant="outlined"
                        fullWidth
                        style={{
                            textTransform: 'none',
                            borderRadius: 8,
                            fontWeight: 600,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmSend}
                        color="primary"
                        variant="contained"
                        fullWidth
                        style={{
                            textTransform: 'none',
                            borderRadius: 8,
                            fontWeight: 600,
                        }}
                    >
                        Send Message
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
